import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import * as Yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { ReactHookForm } from "../../../../components/hook-form/react-hook-form.component";
import { useReactHookForm } from "../../../../hooks/useReactHookForm";
import { AddNewPriceFields } from "./add-new-price-form.component";
import { FormGridEditable } from "../../../../components/hook-form/form-grid-editable.component";
import { servicesEditableColumns } from "../../../services/grid-editable-columns";
import { GridColumnConfig } from "../../../../models/gridCoumnConfig";
import { useAuthorize } from "../../../../hooks/use-authorize";
import { UserPermissionName } from "../../../../models/user-permission-name";
import { useLoadData } from "../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../models/api-response";
import { servicesApiKeyStore } from "../../../../api/masters/services-api-key-store";
import { useSaveData } from "../../../../hooks/useSaveData";
import { projectsApiKeyStore } from "../../../../api/projects-api-key-store";
import { MutationKeyConfig } from "../../../../api/api-helper";
import { InvalidateRoute } from "../../../../hooks/useDeleteData";
import OfficeServiceNotReadyFields from "../office-service-not-ready-fields.component";
import { useEffect, useState } from "react";
import ReportIcon from '@mui/icons-material/Report';


const styling = {
    dialogTitleStyle: { padding: '16px 24px 0' },
    contentPadding: { paddingTop: '30px' },
    buttonPadding: { paddingTop: '20px' },
    tabHeading: { fontWeight: 'bold' },
    buttonStyle: { width: '130px' },
};

const servicePriceValidationSchema1 = Yup.object().shape({
    pricingListName: Yup.string().required('Pricing list name is required'),
    pricingStartDate: Yup.string().required('Pricing start date is required'),
});

const servicePriceValidationSchema2 = Yup.object().shape({
    pricingListName: Yup.string().required('Pricing list name is required'),
    pricingStartDate: Yup.string().required('Pricing start date is required'),
    priceListToEnd: Yup.object()
        .shape({
            pricingListID: Yup.string()
                .nullable()
                .required('Select Price List To End is required')
                .test('not-zero', 'Select Price List To End is required', (value) => value !== '' && value !== '0'),
        })
        .required('Select Price List To End is required'),
});

interface CellProps {
    servicePricingData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface AddNewPriceDialog {
    openDialog: boolean;
    title: string;
    isEdit: boolean;
    officeID: any;
    pricingID: any;
    onSavePricing: () => void;
    onClosedDialog: () => void;
}

export const AddNewPriceDialog = ({
    openDialog,
    title,
    isEdit,
    officeID,
    pricingID,
    onSavePricing,
    onClosedDialog
}: AddNewPriceDialog) => {

    const isDisableEditableColumn = useAuthorize([UserPermissionName.EDIT_SREVICE_PRICE_OFFICE]);

    const customColumns: GridColumnConfig[] = [
        {
            title: 'Invoice Amount ($)', // Main heading
            field: '', // Leave empty as it is a group header
            filter: 'text',
            isHideColumnMenu: true,
            children: [
                {
                    title: 'Service',
                    field: 'price',
                    filter: 'text',
                    fieldType: 'currency',
                    editable: true,
                    editor: 'numeric',
                    isDecimal: true,
                    isDisable: !isDisableEditableColumn,
                },
                {
                    title: 'Retest',
                    field: 'invoiceRetestAmount',
                    filter: 'text',
                    fieldType: 'currency',
                    editable: true,
                    editor: 'numeric',
                    isDecimal: true,
                    isDisable: !isDisableEditableColumn,
                },
            ],
        },
        {
            title: 'Accrual Amount ($)', // Main heading for accrual
            field: '', // Leave empty as it is a group header
            filter: 'text',
            isHideColumnMenu: true,
            children: [
                {
                    title: 'Service',
                    field: 'accrualAmount',
                    filter: 'text',
                    fieldType: 'currency',
                    editable: true,
                    editor: 'numeric',
                    isDecimal: true,
                    isDisable: !isDisableEditableColumn,
                },
                {
                    title: 'Retest',
                    field: 'accrualRetestAmount',
                    filter: 'text',
                    fieldType: 'currency',
                    editable: true,
                    editor: 'numeric',
                    isDecimal: true,
                    isDisable: !isDisableEditableColumn,
                },
            ],
        },
        {
            title: 'Warning',
            field: 'warningText',
            filter: 'text',
            hide: !isEdit,
            searchable: false,
            isGroupable: false,
            isHideColumnMenu: true
        },
    ];

    const [enableValidation, setEnableValidation] = useState<boolean>(false);
    const [services, setServices] = useState<any[]>([]);
    const [originalServiceData, setOriginalService] = useState<any[]>([]);

    const form = useReactHookForm({
        defaultValues: {
            pricingListName: "",
            pricingStartDate: '',
            pricingEndDate: '',
            projects: [],
            pricingListAppliesToLotType: '',
            dryRunFee: "",
            lateCancellationFee: "",
            tripCharge: "",
            endPriorPricingList: "Yes",
            priceListToEnd: null
        },
        yupSchema: isEdit ? servicePriceValidationSchema1 : enableValidation ? servicePriceValidationSchema2 : servicePriceValidationSchema1
    });

    const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
        servicesApiKeyStore.getOfficeServices(!isEdit ? officeID : 0)
    );

    const { data: pricingListData, isLoading: isPricingLoading, isFetching: isPricingFetching } = useLoadData<ApiResponse<any>>(
        projectsApiKeyStore.getProjectPricingDetailsByOfficeID(officeID, isEdit ? pricingID : 0)
    );

    const getServicePriceByName = (serviceData: any, serviceName: any) => {
        const service = serviceData?.find(
            (item: any) => item.serviceName === serviceName
        );
        return service ? service.price : null;
    };

    useEffect(() => {
        if (pricingListData) {
            form.reset({
                pricingListName: pricingListData?.data?.pricingListName ?? "",
                pricingStartDate: pricingListData?.data?.pricingStartDate ?? '',
                pricingEndDate: pricingListData?.data?.pricingEndDate ?? '',
                projects: pricingListData?.data?.pricingListMappedProjects ?? [],
                pricingListAppliesToLotType: pricingListData?.data?.pricingListAppliesToLotType ?? '',
                endPriorPricingList: pricingListData?.data?.endPriorPricingList ?? 'Yes',

                priceListToEnd: null,

                dryRunFee: getServicePriceByName(pricingListData?.data?.services, "Dry Run"),
                lateCancellationFee: getServicePriceByName(pricingListData?.data?.services, "Late Cancellation"),
                tripCharge: getServicePriceByName(pricingListData?.data?.services, "Trip Charge"),
            });

            const filteredServices = pricingListData?.data?.services?.filter(
                (service: any) => !service.isNotReadyService
            );
            setServices(filteredServices);
            setOriginalService(pricingListData?.data?.services);
        }
    }, [pricingListData])

    useEffect(() => {
        if (data) {
            const filtered = data?.data?.filter(
                service => !service.isNotReadyService
            );
            setServices(filtered);
            setOriginalService(data?.data);
        }
    }, [data]);

    const { isLoading: isNewPriceLoading, mutateAsync } = useSaveData(
        isEdit ? projectsApiKeyStore.updatePriceList() : projectsApiKeyStore.createPriceList());

    const handleSubmit = async (filtersData: any) => {

        const notReadyServices =
            originalServiceData
                ?.filter((s: any) => s.isNotReadyService)
                .map((s: any) => ({
                    serviceID: s.serviceID,
                    price:
                        s.serviceName === 'Dry Run'
                            ? filtersData?.dryRunFee
                            : s.serviceName === 'Late Cancellation'
                                ? filtersData?.lateCancellationFee
                                : s.serviceName === 'Trip Charge'
                                    ? filtersData?.tripCharge
                                    : s.price,
                    accrualAmount: s.accrualAmount,
                    invoiceRetestAmount: s.invoiceRetestAmount,
                    accrualRetestAmount: s.accrualRetestAmount,
                })) || [];

        const pricingServices =
            filtersData?.pricingServices?.map((s: any) => ({
                serviceID: s.serviceID,
                price: s.price,
                accrualAmount: s.accrualAmount,
                invoiceRetestAmount: s.invoiceRetestAmount,
                accrualRetestAmount: s.accrualRetestAmount,
            })) || [];

        const payload = {
            officeID,
            ...(isEdit && {
                pricingListID: pricingID,
            }),
            pricingListName: filtersData?.pricingListName || null,
            pricingStartDate: filtersData?.pricingStartDate || null,
            pricingEndDate: filtersData?.pricingEndDate || null,
            pricingListAppliesToLotType: filtersData?.pricingListAppliesToLotType || null,
            pricingListAppliesToProjects:
                filtersData?.projects?.length > 0
                    ? filtersData.projects.map((p: any) => p.projectID)
                    : null,
            services: [...pricingServices, ...notReadyServices],

            // 👇 These properties exist ONLY when isEdit is false
            ...(!isEdit && {
                endPriorPricingList:
                    filtersData?.endPriorPricingList === "Yes"
                        ? true
                        : filtersData?.endPriorPricingList === "No"
                            ? false
                            : null,
                priceListToEnd: filtersData?.priceListToEnd?.pricingListID || null,
            }),
        };

        await mutateAsync({ data: payload });
        onSavePricing();
        onClosedDialog();
    };

    const WarningCell = ({ servicePricingData }: CellProps) => {
        const showWarning = servicePricingData?.pricingListServiceMappingID;

        return (
            <td>
                {!showWarning && (
                    <Tooltip title={<Typography variant="body1">This service is not yet included in the Pricing List and will be added when you save.</Typography>}>
                        <ReportIcon className="btnEdit" style={{ color: '#FE9A37' }} sx={{ fontSize: 30 }} />
                    </Tooltip>
                )}
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'warningText' && isEdit) {
            return (dataItem: any) => {
                return <WarningCell servicePricingData={dataItem.dataItem} />;
            };
        }
    };

    const handleSubmitClick = () => {
        form.handleSubmit(handleSubmit)();
    };

    const handleEnableValidation = (value: boolean) => {
        setEnableValidation(value);
    }

    const servicesEditableColumnsWithPermission: GridColumnConfig[] = [...servicesEditableColumns, ...customColumns];

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Dialog
            open={openDialog}
            fullWidth={true}
            maxWidth={'xl'}
            // onClose={handleClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
        >
            <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
                {title}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClosedDialog}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 3,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <DialogContent dividers style={styling.contentPadding} className="popupDilog">
                <ReactHookForm formName="OFFICE_ADD_NEW_PRICING_FORM" onSubmit={handleSubmit} formContextProps={form}>
                    <Grid container spacing={1}>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <AddNewPriceFields officeID={officeID} isEdit={isEdit} isLoading={isPricingLoading || isPricingFetching} setEnablePrice={handleEnableValidation} />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <Typography variant="body2"><b>SERVICES AND PRICING</b></Typography>
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <OfficeServiceNotReadyFields />
                        </Grid>
                        <Grid item lg={12} sm={12} xl={12} xs={12}>
                            <FormGridEditable
                                fileTitle='office_service'
                                dataItemKey="serviceID"
                                columns={servicesEditableColumnsWithPermission}
                                customGridCell={CustomGridCell}
                                name="pricingServices"
                                dataItems={services || []}
                                isBusy={isLoading || isFetching || isNewPriceLoading || isPricingLoading || isPricingFetching}
                            />
                        </Grid>
                    </Grid>
                </ReactHookForm>
            </DialogContent>
            <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
                <DialogActions className="spaceBetween">
                    <Button variant="outlined" style={{ width: '130px' }} onClick={onClosedDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitClick}
                        variant="contained"
                        style={{ width: '130px' }}
                        disabled={isLoading || isNewPriceLoading || isReadOnlyPermission}>
                        Save
                    </Button>
                </DialogActions>
            </div>
        </Dialog>
    );
}